/**
 * v1.6 / D-KI — KI-Chat an ein Dify-Szenario.
 *
 * AP2 Phase 4 (Run 2): echter Client. Submit → `POST /api/ki/chat` → Bubble;
 * der Server persistiert die Konversation (A-Pfad). Dify-Fehler (502/Timeout,
 * D-107) → Fehler-Bubble, kein Auto-Retry.
 *
 * AP2 Phase 5: der KI-Ordner-Name steht als GROSS­BUCHSTABEN-Überschrift oben,
 * und ein linkes Seitenmenü listet die Chats DIESES KI-Ordners (aus dem
 * `messages`-Store, `GET /ki/conversations`). Ein Klick lädt den Chatverlauf
 * (`GET /ki/messages`). „Neuer Chat" öffnet einen frischen Thread.
 *
 * Der Chat läuft im Scope eines KI-Ordners = ein Ordner, der explizit als
 * KI-Ordner markiert ist (Subtyp, `GET /ki/folders`); `?folder=<id>` wählt vor,
 * sonst per Dropdown. Kein MCP-Token nötig — die Auth trägt die pro Turn
 * signierte Capability. `conversation_id` wird je Thread FE-seitig erzeugt und
 * über alle Turns mitgesendet. Eine Lens ist Pflicht (Dify-`system_prompt`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { uploadFileToFolder } from "../file-actions";
import { navigate } from "../router";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { KiAttachedFile, KiConversation, KiFolder, ListRow } from "../api";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  // Fehler-Bubble (D-107): eine fehlgeschlagene Antwort statt einer echten.
  error?: boolean;
  // ISO-Zeitstempel: geladene Messages tragen `created_at` (Server), live
  // gesendete werden clientseitig gestempelt. Optional — fehlt er, wird keine
  // Zeit gezeigt (der Kopieren-Button bleibt trotzdem).
  createdAt?: string;
}

const newConversationId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `conv-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** A list cell may be a bare string or a {value} object — coerce to text. */
const cellText = (v: unknown): string => {
  if (typeof v === "string") {
    return v;
  }
  if (v && typeof v === "object" && "value" in v) {
    return String((v as { value: unknown }).value ?? "");
  }
  return "";
};

const fmtDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString() : "";

// Bubble-Zeitstempel: sichtbar nur die Uhrzeit (kompakt), das volle Datum steckt
// im `title`/Tooltip — Chats können über mehrere Tage laufen.
const fmtTime = (s?: string): string =>
  s
    ? new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
const fmtStamp = (s?: string): string =>
  s ? new Date(s).toLocaleString() : "";

// Nachricht in die Zwischenablage kopieren; Fallback für Umgebungen ohne
// `navigator.clipboard` (kein Async-Clipboard, z. B. unsicherer Kontext/Tests).
const copyText = async (text: string): Promise<void> => {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    /* fällt unten auf execCommand zurück */
  }
  if (typeof document !== "undefined") {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(ta);
    }
  }
};

// D-121 — the KI-Ordner's system lists, surfaced directly in the header so they
// are discoverable (each is an editable projection over rag_entries/messages).
// Kinds match RyfeKiKnowledge's tabs + the router (?kind=…).
const KI_SYSTEM_LISTS: { kind: string; label: string }[] = [
  { kind: "frage", label: "Fragen" },
  { kind: "anweisung", label: "Anweisungen" },
  { kind: "information", label: "Informationen" },
  { kind: "entscheidung", label: "Entscheidungen" },
  { kind: "sessions", label: "Sessions" },
];

export const RyfeKiChat = () => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;

  const folderParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("folder")
      : null;

  // AP2 Phase 5 (v1.9) — the chat's KI-Ordner are folders explicitly marked as
  // a KI-Ordner (subtype), NOT MCP tokens. Auth is carried by the per-turn
  // signed capability, so no token is needed to chat.
  const [kiFolders, setKiFolders] = useState<KiFolder[]>([]);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(
    folderParam,
  );
  // Lens is MANDATORY before sending (the Dify flow requires system_prompt);
  // the picker lists the workspace's „Lenses" system-list rows.
  const [lenses, setLenses] = useState<ListRow[]>([]);
  const [lensId, setLensId] = useState<string>("");
  // Phase 5 — the chats of the active KI-Ordner (left sidebar).
  const [conversations, setConversations] = useState<KiConversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] =
    useState<string>(newConversationId);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState<KiAttachedFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kurzes „Kopiert!"-Feedback am gerade kopierten Bubble (Index).
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const copyMessage = useCallback(async (idx: number, text: string) => {
    await copyText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1500);
  }, []);

  // Load the workspace's KI-Ordner (marked folders) for the picker.
  useEffect(() => {
    if (!wsId) {
      return;
    }
    ryfeApi
      .listKiFolders(wsId)
      .then((r) => {
        setKiFolders(r.folders);
        // Preselect the ?folder= one, else the first available.
        setActiveFolderId((cur) => {
          if (cur && r.folders.some((f) => f.id === cur)) {
            return cur;
          }
          return r.folders.length > 0 ? r.folders[0].id : null;
        });
      })
      .catch((e) =>
        setError(
          e instanceof ApiError ? e.message : "KI-Ordner laden fehlgeschlagen",
        ),
      );
  }, [wsId]);

  // Load the workspace's lenses (system-list rows) for the mandatory picker.
  useEffect(() => {
    if (!wsId) {
      return;
    }
    ryfeApi
      .getLenses(wsId)
      .then((r) => setLenses(r.rows))
      .catch(() => setLenses([]));
  }, [wsId]);

  const activeFolder = useMemo(
    () => kiFolders.find((f) => f.id === activeFolderId) || null,
    [kiFolders, activeFolderId],
  );
  const kiOrdnerId = activeFolder?.id || null;
  const folderTitle = activeFolder?.name || "KI-Chat";

  // Load the conversations of the active KI-Ordner (sidebar).
  const loadConversations = useCallback(() => {
    if (!wsId || !kiOrdnerId) {
      setConversations([]);
      return;
    }
    ryfeApi
      .listKiConversations(wsId, kiOrdnerId)
      .then((r) => setConversations(r.conversations))
      .catch(() => setConversations([]));
  }, [wsId, kiOrdnerId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Auto-scroll to the newest bubble.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const startNewChat = useCallback(() => {
    setMessages([]);
    setPending([]);
    setError(null);
    setConversationId(newConversationId());
  }, []);

  // A new KI-Ordner scope is a new thread.
  const selectFolder = (id: string) => {
    setActiveFolderId(id);
    setMessages([]);
    setPending([]);
    setError(null);
    setConversationId(newConversationId());
  };

  // Open an existing chat from the sidebar → load its messages.
  const openConversation = async (cid: string) => {
    if (!wsId || !kiOrdnerId) {
      return;
    }
    setError(null);
    setPending([]);
    setConversationId(cid);
    try {
      const r = await ryfeApi.listKiMessages(wsId, kiOrdnerId, cid);
      setMessages(
        r.messages.map((m) => ({
          role: m.role,
          content: m.content,
          createdAt: m.created_at,
        })),
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Chat laden fehlgeschlagen");
      setMessages([]);
    }
  };

  // Attach files via the EXISTING upload path (upload into the KI-Ordner, then
  // reference by {file_id, name, content_type} — never inline bytes, D-81).
  const onAttach = async (files: FileList | null) => {
    if (!files || files.length === 0 || !wsId || !kiOrdnerId) {
      return;
    }
    setBusy(true);
    try {
      const added: KiAttachedFile[] = [];
      for (const file of Array.from(files)) {
        const meta = await uploadFileToFolder(wsId, kiOrdnerId, file);
        added.push({
          file_id: meta.id,
          name: meta.name,
          content_type: meta.content_type ?? null,
        });
      }
      setPending((p) => [...p, ...added]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
      if (fileInput.current) {
        fileInput.current.value = "";
      }
    }
  };

  const send = async () => {
    const query = input.trim();
    if (!query || !wsId || !kiOrdnerId || busy) {
      return;
    }
    // Lens is mandatory (system_prompt is a required Dify variable).
    if (!lensId) {
      setError("Bitte zuerst eine Lens wählen.");
      return;
    }
    const attached = pending;
    setInput("");
    setPending([]);
    setError(null);
    setMessages((m) => [
      ...m,
      { role: "user", content: query, createdAt: new Date().toISOString() },
    ]);
    setBusy(true);
    try {
      const out = await ryfeApi.kiChat(wsId, {
        ki_ordner_id: kiOrdnerId,
        conversation_id: conversationId,
        query,
        lens_id: lensId,
        attached_files: attached.length > 0 ? attached : undefined,
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: out.reply,
          createdAt: new Date().toISOString(),
        },
      ]);
      // A fresh chat now has rows → refresh the sidebar (title + order).
      loadConversations();
    } catch (e) {
      // D-107: Fehler-Bubble, kein Auto-Retry.
      const msg = e instanceof ApiError ? e.message : "KI nicht erreichbar";
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: msg,
          error: true,
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <div className="ryfe-page ryfe-kichat">
      <div className="ryfe-kichat__head">
        {/* Phase 5 — der KI-Ordner-Name als GROSS­BUCHSTABEN-Überschrift. */}
        <h1 className="ryfe-page__title ryfe-kichat__title">{folderTitle}</h1>
        <div className="ryfe-kichat__controls">
          <label className="ryfe-kichat__scope">
            KI-Ordner:
            <select
              className="ryfe-cell-input"
              value={activeFolderId || ""}
              onChange={(e) => selectFolder(e.target.value)}
              disabled={kiFolders.length === 0}
            >
              {kiFolders.length === 0 ? (
                <option value="">— kein KI-Ordner —</option>
              ) : (
                kiFolders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))
              )}
            </select>
          </label>
          <label className="ryfe-kichat__scope">
            Lens:
            <select
              className="ryfe-cell-input"
              value={lensId}
              onChange={(e) => setLensId(e.target.value)}
              disabled={lenses.length === 0}
            >
              <option value="">
                {lenses.length === 0
                  ? "— keine Lens vorhanden —"
                  : "— wählen —"}
              </option>
              {lenses.map((l) => (
                <option key={l.id} value={l.id}>
                  {cellText(l.data.Name) || "(ohne Namen)"}
                </option>
              ))}
            </select>
          </label>
        </div>
        {/* D-121 — Systemlisten des KI-Ordners direkt im Header sichtbar; jeder
            Eintrag öffnet die editierbare Wissens-Seite (Projektion über
            rag_entries/messages) an diesem Reiter. */}
        {kiOrdnerId && (
          <nav
            className="ryfe-kichat__systemlists"
            aria-label="Systemlisten des KI-Ordners"
          >
            <span className="ryfe-kichat__systemlists-label">
              Systemlisten:
            </span>
            {KI_SYSTEM_LISTS.map((s) => (
              <button
                key={s.kind}
                type="button"
                className="ryfe-kichat__systemlist"
                onClick={() =>
                  navigate(
                    `/ki-knowledge?folder=${encodeURIComponent(
                      kiOrdnerId,
                    )}&kind=${s.kind}`,
                  )
                }
              >
                {s.label}
              </button>
            ))}
          </nav>
        )}
      </div>

      {error && <div className="ryfe-auth__error">{error}</div>}

      {!kiOrdnerId ? (
        <div className="ryfe-panel">
          <p className="ryfe-muted" style={{ padding: 0 }}>
            Noch kein KI-Ordner. Markiere unter „Meine Dateien" einen Ordner per
            ⋮-Menü → „Als KI-Ordner" (nur Ordner mit Schreibrecht können
            chatten).
          </p>
        </div>
      ) : (
        <div className="ryfe-kichat__body">
          <aside className="ryfe-kichat__sidebar">
            <button
              className="ryfe-btn ryfe-btn--primary ryfe-kichat__newchat"
              onClick={startNewChat}
            >
              + Neuer Chat
            </button>
            <div className="ryfe-kichat__chats">
              {conversations.length === 0 ? (
                <div className="ryfe-muted">Noch keine Chats.</div>
              ) : (
                conversations.map((c) => (
                  <button
                    key={c.conversation_id}
                    className={`ryfe-kichat__chat${
                      c.conversation_id === conversationId
                        ? " ryfe-kichat__chat--active"
                        : ""
                    }`}
                    onClick={() => void openConversation(c.conversation_id)}
                  >
                    <span className="ryfe-kichat__chat-title">
                      {c.title || "(neuer Chat)"}
                    </span>
                    <span className="ryfe-kichat__chat-meta">
                      {fmtDate(c.last_at)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </aside>

          <div className="ryfe-kichat__main">
            <div className="ryfe-kichat__log" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="ryfe-muted">
                  Noch keine Nachrichten in diesem Chat.
                </div>
              ) : (
                messages.map((m, i) => (
                  <div
                    key={i}
                    className={`ryfe-kichat__bubble ryfe-kichat__bubble--${
                      m.role
                    }${m.error ? " ryfe-kichat__bubble--error" : ""}`}
                  >
                    <div className="ryfe-kichat__bubble-text">{m.content}</div>
                    <div className="ryfe-kichat__bubble-foot">
                      {m.createdAt && (
                        <time
                          className="ryfe-kichat__bubble-time"
                          dateTime={m.createdAt}
                          title={fmtStamp(m.createdAt)}
                        >
                          {fmtTime(m.createdAt)}
                        </time>
                      )}
                      <button
                        type="button"
                        className="ryfe-kichat__copy"
                        onClick={() => void copyMessage(i, m.content)}
                        title="Nachricht kopieren"
                        aria-label="Nachricht kopieren"
                      >
                        {copiedIdx === i ? "✓ Kopiert" : "⧉ Kopieren"}
                      </button>
                    </div>
                  </div>
                ))
              )}
              {busy && <div className="ryfe-muted ryfe-kichat__typing">…</div>}
            </div>

            {pending.length > 0 && (
              <div className="ryfe-kichat__attachments">
                {pending.map((a) => (
                  <span key={a.file_id} className="ryfe-chip">
                    📎 {a.name}
                  </span>
                ))}
              </div>
            )}

            <div className="ryfe-kichat__composer">
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => void onAttach(e.target.files)}
              />
              <button
                className="ryfe-btn ryfe-btn--ghost"
                onClick={() => fileInput.current?.click()}
                disabled={busy}
                title="Datei anhängen"
              >
                📎
              </button>
              <textarea
                className="ryfe-cell-input ryfe-kichat__input"
                rows={2}
                placeholder="Nachricht… (Enter senden, Shift+Enter = Zeilenumbruch)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={busy}
              />
              <button
                className="ryfe-btn ryfe-btn--primary"
                onClick={() => void send()}
                disabled={busy || input.trim().length === 0 || !lensId}
                title={!lensId ? "Bitte zuerst eine Lens wählen" : undefined}
              >
                {busy ? "…" : "Senden"}
              </button>
            </div>
            {lenses.length === 0 && (
              <p className="ryfe-muted" style={{ marginTop: "0.4rem" }}>
                Für den Chat wird eine Lens benötigt (liefert den Systemprompt).
                Lege zunächst unter „Lenses" eine an.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
